import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const LANGUAGE_STORAGE_KEY = 'ntf-language';

export const LANGUAGE_OPTIONS = [
  { code: 'ka', flag: '🇬🇪', label: 'ქართული', htmlLang: 'ka-GE' },
  { code: 'en', flag: '🇬🇧', label: 'English', htmlLang: 'en-GB' },
  { code: 'ru', flag: '🇷🇺', label: 'Русский', htmlLang: 'ru-RU' }
];

const translations = {
  ka: {
    language: { select: 'აირჩიე ენა' },
    nav: {
      comment: 'დღის კომენტარი',
      forecast: 'დღის პროგნოზი',
      weekly: 'კვირის პროგნოზი',
      live: 'LIVE'
    },
    live: { history: 'ისტორია' },
    forecast: {
      loading: 'იტვირთება პროგნოზები...',
      clubs: 'კლუბები',
      prediction: 'პროგნოზი',
      coefficientDate: 'კოეფ. | თარიღი',
      noForecasts: 'პროგნოზები არ მოიძებნა',
      noForecastsMessage: 'არჩეულ თარიღზე პროგნოზები არ არის ხელმისაწვდომი.',
      history: 'თამაშების ისტორია',
      noHistory: 'ისტორია არ მოიძებნა',
      noHistoryFiltered: 'ფილტრების მიხედვით შედეგები არ მოიძებნა.',
      noHistoryAvailable: 'თამაშების ისტორია ჯერ არ არის ხელმისაწვდომი.'
    },
    filters: {
      single: 'ერთი დღე',
      range: 'პერიოდი',
      month: 'მთელი თვე',
      chooseDate: 'აირჩიეთ თარიღი',
      start: 'დასაწყისი',
      end: 'დასასრული',
      chooseClub: 'აირჩიე კლუბი',
      allClubs: 'ყველა კლუბი',
      searchClub: 'მოძებნე კლუბი...',
      clubNotFound: 'კლუბი ვერ მოიძებნა'
    },
    common: { clear: 'გასუფთავება' }
  },
  en: {
    language: { select: 'Choose language' },
    nav: {
      comment: 'Daily Comment',
      forecast: 'Daily Forecast',
      weekly: 'Weekly Forecast',
      live: 'LIVE'
    },
    live: { history: 'History' },
    forecast: {
      loading: 'Loading forecasts...',
      clubs: 'Clubs',
      prediction: 'Prediction',
      coefficientDate: 'Odds | Date',
      noForecasts: 'No forecasts found',
      noForecastsMessage: 'No forecasts are available for the selected date.',
      history: 'Match History',
      noHistory: 'No history found',
      noHistoryFiltered: 'No results match the selected filters.',
      noHistoryAvailable: 'Match history is not available yet.'
    },
    filters: {
      single: 'One day',
      range: 'Period',
      month: 'Whole month',
      chooseDate: 'Choose a date',
      start: 'Start',
      end: 'End',
      chooseClub: 'Choose a club',
      allClubs: 'All clubs',
      searchClub: 'Search for a club...',
      clubNotFound: 'Club not found'
    },
    common: { clear: 'Clear' }
  },
  ru: {
    language: { select: 'Выберите язык' },
    nav: {
      comment: 'Комментарий дня',
      forecast: 'Прогноз дня',
      weekly: 'Недельный прогноз',
      live: 'LIVE'
    },
    live: { history: 'История' },
    forecast: {
      loading: 'Загрузка прогнозов...',
      clubs: 'Команды',
      prediction: 'Прогноз',
      coefficientDate: 'Коэфф. | Дата',
      noForecasts: 'Прогнозы не найдены',
      noForecastsMessage: 'На выбранную дату прогнозы недоступны.',
      history: 'История матчей',
      noHistory: 'История не найдена',
      noHistoryFiltered: 'По выбранным фильтрам ничего не найдено.',
      noHistoryAvailable: 'История матчей пока недоступна.'
    },
    filters: {
      single: 'Один день',
      range: 'Период',
      month: 'Весь месяц',
      chooseDate: 'Выберите дату',
      start: 'Начало',
      end: 'Конец',
      chooseClub: 'Выберите команду',
      allClubs: 'Все команды',
      searchClub: 'Найти команду...',
      clubNotFound: 'Команда не найдена'
    },
    common: { clear: 'Очистить' }
  }
};

const LanguageContext = createContext(null);

const getInitialLanguage = () => {
  try {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return translations[storedLanguage] ? storedLanguage : 'ka';
  } catch {
    return 'ka';
  }
};

const getTranslation = (language, key) => {
  return key.split('.').reduce((value, part) => value?.[part], translations[language]) || key;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    if (!translations[nextLanguage]) return;

    setLanguageState(nextLanguage);
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    } catch {
      // The app still switches languages when storage is unavailable.
    }
  }, []);

  useEffect(() => {
    const selectedLanguage = LANGUAGE_OPTIONS.find(option => option.code === language);
    document.documentElement.lang = selectedLanguage?.htmlLang || 'ka-GE';
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t: (key) => getTranslation(language, key)
  }), [language, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }
  return context;
};
